/**
 * Normalizes `collection_results` (and legacy `results` / `feature_results`) into
 * shapes suitable for the project overview dashboard.
 */

import { pickLatestCollectionRun } from '../../../../utils/collectionRuns';

export interface OntologyDomainRow {
  key: string;
  risk_domain: string;
  risk_level: string;
  interpretation?: string | string[];
}

export interface SummaryKpis {
  ontologyOverall: string | null;
  biasVerdict: string | null;
  biasScore: number | null;
  abuseVerdict: string | null;
  abuseIndex: number | null;
  fairnessLlmScore: number | null;
  fairnessLlmVerdict: string | null;
  externalCompliance: string | null;
  internalCompliance: string | null;
  owaspStatus: string | null;
  owaspPassCount: number;
  owaspFailCount: number;
  audioDecision: string | null;
  audioDeepfakeRisk: number | null;
}

export interface BiasInsight {
  datasetVerdict: string | null;
  microeaiBiasScore: number | null;
  verdictDistribution: Record<string, number>;
  llmVerdictSample: string | null;
  llmBiasRiskScore: number | null;
}

export interface AbuseInsight {
  overallVerdict: string | null;
  overallAbuseIndex: number | null;
  driftSeries: { eventIndex: number; abuseScorePercent: number }[];
  mlHighRiskCount: number;
}

export interface FairnessInsight {
  llmRows: Array<Record<string, unknown>>;
  mlRows: Array<Record<string, unknown>>;
}

export interface ExplainabilityInsight {
  llmShap: number | null;
  mlShap: number | null;
  llmVerdict: string | null;
  limeStability: string | null;
}

export interface ComplianceInsight {
  externalStatus: string | null;
  internalStatus: string | null;
  externalRows: Array<Record<string, unknown>>;
  internalRows: Array<Record<string, unknown>>;
}

export interface OwaspInsight {
  overallStatus: string | null;
  controls: Array<{
    name: string;
    status: string;
    severity: string;
    score: number;
    threshold: number;
  }>;
}

export interface AudioInsight {
  decision: string | null;
  deepfakeRisk: number | null;
  policyViolations: string[];
  transcriptSnippet: string | null;
}

export interface ProjectCollectionInsights {
  kpis: SummaryKpis;
  ontologyDomains: OntologyDomainRow[];
  bias: BiasInsight;
  abuse: AbuseInsight;
  fairness: FairnessInsight;
  explainability: ExplainabilityInsight;
  compliance: ComplianceInsight;
  owasp: OwaspInsight;
  audio: AudioInsight;
}

function getCollectionBucket(projectResults: unknown): Record<string, unknown> | null {
  if (!projectResults || typeof projectResults !== 'object') return null;
  const pr = projectResults as Record<string, unknown>;
  const a = pr.collection_results;
  if (a && typeof a === 'object') return a as Record<string, unknown>;
  const b = pr.results;
  if (b && typeof b === 'object') return b as Record<string, unknown>;
  const c = pr.feature_results;
  if (c && typeof c === 'object') return c as Record<string, unknown>;
  return null;
}

function safeNum(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function extractOntologyDomains(raw: unknown): OntologyDomainRow[] {
  const o = pickLatestCollectionRun(raw as any);
  if (!o || typeof o !== 'object') return [];
  const obj = o as Record<string, unknown>;
  const domains = obj.domains ?? obj.reasoning_layer;
  if (!domains || typeof domains !== 'object') return [];
  return Object.entries(domains as Record<string, unknown>).map(([key, domain]) => {
    const d = domain as Record<string, unknown>;
    return {
      key,
      risk_domain: String(d.risk_domain ?? d.riskDomain ?? key),
      risk_level: String(d.risk_level ?? d.riskLevel ?? 'UNKNOWN'),
      interpretation: d.interpretation as string | string[] | undefined
    };
  });
}

function extractBias(projectResults: unknown, bucket: Record<string, unknown> | null): BiasInsight {
  const biasArr = bucket?.bias;
  const biasItem = pickLatestCollectionRun(biasArr as any) as Record<string, unknown> | null;
  let aggregate: Record<string, unknown> | null = null;
  let llmBlock: Record<string, unknown> | null = null;

  if (biasItem && typeof biasItem === 'object') {
    const llmBias = biasItem.LLM_BIAS ?? (biasItem as any).llm_bias;
    if (Array.isArray(llmBias) && llmBias.length) {
      llmBlock = llmBias[0] as Record<string, unknown>;
    } else if (llmBias && typeof llmBias === 'object') {
      llmBlock = llmBias as Record<string, unknown>;
    }
    const agg =
      (biasItem as any).aggregate_summary ??
      (llmBlock as any)?.aggregate_summary ??
      (Array.isArray(llmBias) ? (llmBias as any[]).find((x: any) => x?.aggregate_summary)?.aggregate_summary : null);
    if (agg && typeof agg === 'object') aggregate = agg as Record<string, unknown>;
  }

  const verdictDistribution: Record<string, number> = {};
  const vd = aggregate?.verdict_distribution;
  if (vd && typeof vd === 'object') {
    for (const [k, val] of Object.entries(vd as Record<string, unknown>)) {
      const n = safeNum(val);
      if (n != null) verdictDistribution[k] = n;
    }
  }

  const ba = llmBlock?.bias_analysis as Record<string, unknown> | undefined;
  return {
    datasetVerdict: aggregate?.dataset_verdict != null ? String(aggregate.dataset_verdict) : null,
    microeaiBiasScore: safeNum(aggregate?.microeai_bias_score),
    verdictDistribution,
    llmVerdictSample: ba?.Verdict != null ? String(ba.Verdict) : null,
    llmBiasRiskScore: safeNum(ba?.Bias_Risk_Score)
  };
}

function extractAbuse(bucket: Record<string, unknown> | null): AbuseInsight {
  const safetyArr = bucket?.ai_safety ?? bucket?.abuse_ai;
  const item = pickLatestCollectionRun(safetyArr as any) as Record<string, unknown> | null;
  if (!item) {
    return {
      overallVerdict: null,
      overallAbuseIndex: null,
      driftSeries: [],
      mlHighRiskCount: 0
    };
  }

  if (item.risk_score != null || item.score_breakdown != null) {
    return {
      overallVerdict: item.dataset_verdict != null ? String(item.dataset_verdict) : null,
      overallAbuseIndex: safeNum(item.risk_score),
      driftSeries: [],
      mlHighRiskCount: 0
    };
  }

  const llm = item.LLM_ABUSE_ANALYSIS as Record<string, unknown> | undefined;
  const summary = llm?.Summary as Record<string, unknown> | undefined;
  const driftRaw = llm?.Drift;
  const driftSeries: { eventIndex: number; abuseScorePercent: number }[] = [];
  if (Array.isArray(driftRaw)) {
    for (const row of driftRaw) {
      if (!row || typeof row !== 'object') continue;
      const r = row as Record<string, unknown>;
      const ei = safeNum(r.Event_Index ?? r.event_index) ?? 0;
      const pct = safeNum(r.Abuse_Score_Percent ?? r.abuse_score_percent) ?? 0;
      driftSeries.push({ eventIndex: ei, abuseScorePercent: pct });
    }
    driftSeries.sort((a, b) => a.eventIndex - b.eventIndex);
  }

  const ml = item.ML_ABUSE_DETECTION ?? item.ML_Abuse_Detection;
  let mlHighRiskCount = 0;
  if (Array.isArray(ml)) {
    mlHighRiskCount = ml.filter((row: any) => {
      const risk = String(row?.Risk ?? row?.risk ?? '').toLowerCase();
      return risk === 'high';
    }).length;
  }

  return {
    overallVerdict: summary?.Overall_Verdict != null ? String(summary.Overall_Verdict) : null,
    overallAbuseIndex: safeNum(summary?.Overall_Abuse_Index),
    driftSeries,
    mlHighRiskCount
  };
}

function extractFairness(bucket: Record<string, unknown> | null): FairnessInsight {
  const fm = pickLatestCollectionRun(bucket?.fairness_metric as any) as Record<string, unknown> | null;
  if (!fm) return { llmRows: [], mlRows: [] };
  const llm = fm.LLM_Fairness_Metric ?? fm.llm_fairness_metric;
  const ml = fm.ML_Fairness_Metric ?? fm.ml_fairness_metric;
  return {
    llmRows: Array.isArray(llm) ? (llm as Array<Record<string, unknown>>) : [],
    mlRows: Array.isArray(ml) ? (ml as Array<Record<string, unknown>>) : []
  };
}

function extractExplainability(bucket: Record<string, unknown> | null): ExplainabilityInsight {
  const ex = pickLatestCollectionRun(bucket?.explainability as any) as Record<string, unknown> | null;
  if (!ex) {
    return { llmShap: null, mlShap: null, llmVerdict: null, limeStability: null };
  }
  const llm = ex.LLM_Model_Explainability as Record<string, unknown> | undefined;
  const ml = ex.ML_Model_Explainability as Record<string, unknown> | undefined;
  const me = llm?.microeai_explainability as Record<string, unknown> | undefined;
  return {
    llmShap: safeNum(llm?.SHAP_Score ?? llm?.shap_score),
    mlShap: safeNum(ml?.SHAP_Score ?? ml?.shap_score),
    llmVerdict: me?.verdict != null ? String(me.verdict) : null,
    limeStability: llm?.lime_stability != null ? String(llm.lime_stability) : null
  };
}

function extractCompliance(bucket: Record<string, unknown> | null): ComplianceInsight {
  const ext = pickLatestCollectionRun(bucket?.external_compliance as any) as Record<string, unknown> | null;
  const intl = pickLatestCollectionRun(bucket?.internal_compliance as any) as Record<string, unknown> | null;
  const extList = ext?.external_compliance ?? ext?.violations;
  const intList = intl?.internal_compliance ?? intl?.items;
  return {
    externalStatus: ext?.overall_external_status != null ? String(ext.overall_external_status) : null,
    internalStatus: intl?.overall_internal_status != null ? String(intl.overall_internal_status) : null,
    externalRows: Array.isArray(extList) ? (extList as Array<Record<string, unknown>>) : [],
    internalRows: Array.isArray(intList) ? (intList as Array<Record<string, unknown>>) : []
  };
}

function extractOwasp(bucket: Record<string, unknown> | null): OwaspInsight {
  const o = pickLatestCollectionRun(bucket?.owasp_mlp as any) as Record<string, unknown> | null;
  if (!o) return { overallStatus: null, controls: [] };
  const controlsRaw = o.controls;
  const controls: OwaspInsight['controls'] = [];
  if (Array.isArray(controlsRaw)) {
    for (const c of controlsRaw) {
      if (!c || typeof c !== 'object') continue;
      const row = c as Record<string, unknown>;
      controls.push({
        name: String(row.name ?? row.code ?? 'Control'),
        status: String(row.status ?? '—'),
        severity: String(row.severity ?? '—'),
        score: safeNum(row.score) ?? 0,
        threshold: safeNum(row.threshold) ?? 0
      });
    }
  }
  return {
    overallStatus: o.overall_status != null ? String(o.overall_status) : null,
    controls
  };
}

function extractAudio(bucket: Record<string, unknown> | null): AudioInsight {
  const a = pickLatestCollectionRun(bucket?.audio_deepfake as any) as Record<string, unknown> | null;
  const analysis = a?.analysis as Record<string, unknown> | undefined;
  if (!analysis) {
    return { decision: null, deepfakeRisk: null, policyViolations: [], transcriptSnippet: null };
  }
  const t = analysis.transcript != null ? String(analysis.transcript) : null;
  const snippet = t && t.length > 220 ? `${t.slice(0, 217)}…` : t;
  const pv = analysis.policy_violations;
  return {
    decision: analysis.decision != null ? String(analysis.decision) : null,
    deepfakeRisk: safeNum(analysis.deepfake_risk),
    policyViolations: Array.isArray(pv) ? pv.map(String) : [],
    transcriptSnippet: snippet
  };
}

export function gatherCollectionInsights(projectResults: unknown): ProjectCollectionInsights {
  const bucket = getCollectionBucket(projectResults);

  const ontologyRaw = bucket?.ontology;
  const ontologyDomains = extractOntologyDomains(ontologyRaw);
  const ontologyFirst = pickLatestCollectionRun(ontologyRaw as any) as Record<string, unknown> | null;
  const ontologyOverall = ontologyFirst?.overall_risk != null ? String(ontologyFirst.overall_risk) : null;

  const bias = extractBias(projectResults, bucket);
  const abuse = extractAbuse(bucket);
  const fairness = extractFairness(bucket);
  const explainability = extractExplainability(bucket);
  const compliance = extractCompliance(bucket);
  const owasp = extractOwasp(bucket);
  const audio = extractAudio(bucket);

  const llmFair0 = fairness.llmRows[0] as Record<string, unknown> | undefined;
  const fairnessLlmScore = safeNum(llmFair0?.['Fairness Score'] ?? llmFair0?.fairness_score);
  const fairnessLlmVerdict =
    llmFair0?.Verdict != null ? String(llmFair0.Verdict) : llmFair0?.verdict != null ? String(llmFair0.verdict) : null;

  const owaspPassCount = owasp.controls.filter(c => c.status?.toUpperCase() === 'PASS').length;
  const owaspFailCount = owasp.controls.filter(c => c.status?.toUpperCase() === 'FAIL').length;

  const kpis: SummaryKpis = {
    ontologyOverall,
    biasVerdict: bias.datasetVerdict ?? bias.llmVerdictSample,
    biasScore: bias.microeaiBiasScore ?? bias.llmBiasRiskScore,
    abuseVerdict: abuse.overallVerdict,
    abuseIndex: abuse.overallAbuseIndex,
    fairnessLlmScore,
    fairnessLlmVerdict,
    externalCompliance: compliance.externalStatus,
    internalCompliance: compliance.internalStatus,
    owaspStatus: owasp.overallStatus,
    owaspPassCount,
    owaspFailCount,
    audioDecision: audio.decision,
    audioDeepfakeRisk: audio.deepfakeRisk
  };

  return {
    kpis,
    ontologyDomains,
    bias,
    abuse,
    fairness,
    explainability,
    compliance,
    owasp,
    audio
  };
}
